import { React, useState, useEffect, useCallback, useMemo } from "react";
import "./Analytics.css";
import { SidebarCol } from "../../chatMain/Sidebar/Sidebar.jsx";
import { MainHeader } from "../../chatMain/MainHeader/MainHeader.jsx";
import api from "../../../axiosconfig.js";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Chart } from "../../Assessment/Graphing/Chart.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons";
import Markdown from "react-markdown";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { DomainEngagementTable } from "./domainEngagement/DomainEngagementTable.jsx";
import { IconButton, TextField, MenuItem, ButtonGroup, Button } from "@mui/material";
import { isAdminUser } from "../../../utils/userUtils";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import {
  getCompanyAssessmentEngagement,
  getCompanyInterfaceUsage,
  getCompanyLiveSessionEngagement,
  getCompanyQuestionsAnswered,
} from "../../../api/usage/usageApi.js";
import {
  getCompanyDomainCounts,
} from "../../../api/checkins/checkinsApi.js";
import MonthRangeSelector from "../../../components/pearli/MonthRangeSelector.jsx";
import WellnessCheckinSection from "../../../components/checkins/WellnessCheckinSection.jsx";
import {
  compareMonthKeys,
  getCurrentMonthKey,
  getMonthKey,
  getRecentMonthOptions,
  getValidatedMonthRange,
  isSelectableMonthKey,
  monthKeyToDate,
  monthKeyToLabel,
  monthKeyToRange,
} from "../../../utils/dateUtils.js";

export const Analytics = () => {
  //REACT variables
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const showAdminButtons = isAdminUser(user);
  const [isPageVisible, setIsPageVisible] = useState(() => isAdminUser(user));

  //Statistics Variables
  const [numEmployees, setNumEmployees] = useState("...");
  const [numActiveEmployees, setNumActiveEmployees] = useState("...");
  const [dailyEngagement, setDailyEngagement] = useState("...");
  const [mindfulnessUsage, setMindfulnessUsage] = useState(["...", "..."]);
  const [liveEventsUsage, setLiveEventsUsage] = useState("...");

  const getWellnessAverages = (responseData) =>
    responseData?.wellnessAverages || responseData?.wellness_averages || {};
  const [chatUsage, setChatUsage] = useState("...");
  const [assessmentUsage, setAssessmentUsage] = useState("...");
  const [engagementStats, setEngagementStats] = useState(["...", "..."]);
  const [scores, setScores] = useState([0, 0, 0, 0, 0, 0]);
  const [comparisonScores, setComparisonScores] = useState([
    [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
  ]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const toggleFilters = () => {
    setFiltersOpen((prev) => !prev);
  };
  const [filterAge, setFilterAge] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterDesc, setFilterDesc] = useState("");
  const [filterJob, setFilterJob] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterInterest, setFilterInterest] = useState("");

  const applyFilters = () => {
    // append filters to your existing API building logic
    // fetchPearliAverages({
    //   age: filterAge,
    //   gender: filterGender,
    //   location: filterLocation,
    //   description: filterDesc,
    //   job_title: filterJob,
    //   company: filterCompany,
    //   interest: filterInterest,
    // });
  };

  const clearAllFilters = () => {
    setFilterAge("");
    setFilterGender("");
    setFilterLocation("");
    setFilterDesc("");
    setFilterJob("");
    setFilterCompany("");
    setFilterInterest("");
  };


  const [summaries, setSummaries] = useState([
    "Loading Summary...",
    "Loading Summary...",
    "Loading Summary...",
    "Loading Summary...",
    "Loading Summary...",
    "Loading Summary...",
    "Loading Summary...",
  ]);

  //Variables for PEARLI distribution
  const [pearliLoad, setPearliLoad] = useState(false);
  const [pDistChange, setPDistChange] = useState([0, 0, 0, 0]);
  const [eDistChange, setEDistChange] = useState([0, 0, 0, 0]);
  const [aDistChange, setADistChange] = useState([0, 0, 0, 0]);
  const [rDistChange, setRDistChange] = useState([0, 0, 0, 0]);
  const [lDistChange, setLDistChange] = useState([0, 0, 0, 0]);
  const [iDistChange, setIDistChange] = useState([0, 0, 0, 0]);

  //Date Variable
  const today = useMemo(() => new Date(), []);
  const currentMonthKey = useMemo(() => getCurrentMonthKey(today), [today]);
  const defaultMonthRange = useMemo(
    () => getValidatedMonthRange({ anchorDate: today }),
    [today]
  );
  const monthOptions = useMemo(
    () => getRecentMonthOptions({ anchorDate: today, includeCurrentMonth: true }),
    [today]
  );
  const [focusDate, setFocusDate] = useState(new Date());
  const [maxDate, setMaxDate] = useState(new Date());
  const [previousDate, setPreviousDate] = useState(new Date());
  const [rangePickerStart, setRangePickerStart] = useState(null);
  const [rangePickerEnd, setRangePickerEnd] = useState(null);
  const [rangeStartMonth, setRangeStartMonth] = useState(defaultMonthRange.startKey);
  const [rangeEndMonth, setRangeEndMonth] = useState(defaultMonthRange.endKey);
  const lastCompletedMonthEnd = useMemo(() => {
    return new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
  }, [today]);
  const options = { year: "numeric", month: "long" };
  const optionsShort = { year: "numeric", month: "short" };
  const [displayDateString, setDisplayDateString] = useState(
    monthKeyToLabel(currentMonthKey)
  );
  const [dataViewMode, setDataViewMode] = useState("current");

  const startOfMonthDate = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const endOfMonthDate = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  const localCalendarDateToUtcDate = (value, end = false) => {
    const date = value instanceof Date ? value : new Date(value);
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        end ? 23 : 0,
        end ? 59 : 0,
        end ? 59 : 0,
        end ? 999 : 0
      )
    );
  };
  const toUtcString = (date, end = false) =>
    localCalendarDateToUtcDate(date, end).toUTCString();
  const toLocalIsoDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    // Dates in this module are serialized as UTC boundaries for API payloads.
    // Read them back using UTC fields to avoid local timezone day rollovers.
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getMonthSpanInclusive = (startDate, endDate) => {
    const yearDiff = endDate.getFullYear() - startDate.getFullYear();
    const monthDiff = endDate.getMonth() - startDate.getMonth();
    return yearDiff * 12 + monthDiff + 1;
  };

  const buildComparisonWindows = (rangeStart, rangeEnd) => {
    const currentStart = startOfMonthDate(rangeStart);
    const currentEnd = endOfMonthDate(rangeEnd);
    const spanMonths = getMonthSpanInclusive(currentStart, currentEnd);

    const previousStart = startOfMonthDate(
      new Date(currentStart.getFullYear(), currentStart.getMonth() - spanMonths, 1)
    );
    const previousEnd = endOfMonthDate(
      new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1)
    );

    return {
      currentStart,
      currentEnd,
      previousStart,
      previousEnd,
    };
  };
  
  const getDomainMonthApiRange = (monthKey) => {
    const range = monthKeyToRange(monthKey);
    if (!range) return null;
  
    if (monthKey === currentMonthKey) {
      return {
        start: range.start,
        end: toLocalIsoDate(today),
      };
    }
  
    return range;
  };

  const applyAnalyticsMonthRange = useCallback(
    (nextStartMonth, nextEndMonth) => {
      if (!nextStartMonth || !nextEndMonth) return;
      if (
        !isSelectableMonthKey(nextStartMonth, {
          anchorDate: today,
          maxMonthKey: currentMonthKey,
        }) ||
        !isSelectableMonthKey(nextEndMonth, {
          anchorDate: today,
          maxMonthKey: currentMonthKey,
        })
      ) {
        return;
      }
      if (compareMonthKeys(nextStartMonth, nextEndMonth) > 0) return;

      const nextRangeStart = monthKeyToDate(nextStartMonth);
      const nextRangeEnd = monthKeyToDate(nextEndMonth);
      if (!nextRangeStart || !nextRangeEnd) return;

      const comparison = buildComparisonWindows(nextRangeStart, nextRangeEnd);
      const currentRangeEnd =
        nextEndMonth === currentMonthKey ? new Date(today) : comparison.currentEnd;

      setRangeStartMonth(nextStartMonth);
      setRangeEndMonth(nextEndMonth);
      setPreviousDate(nextRangeStart);
      setFocusDate(currentRangeEnd);
      setRangePickerStart(comparison.currentStart);
      setRangePickerEnd(currentRangeEnd);
      setDisplayDateString(
        `${monthKeyToLabel(nextStartMonth)} - ${monthKeyToLabel(nextEndMonth)}`
      );

      newLoadData();
      populateData(
        toUtcString(comparison.currentStart, false),
        toUtcString(currentRangeEnd, true),
        toUtcString(comparison.previousStart, false),
        toUtcString(comparison.previousEnd, true),
        {
          startMonthKey: nextStartMonth,
          endMonthKey: nextEndMonth,
        }
      );
    },
    [currentMonthKey, today]
  );

  const toFiniteNumber = useCallback((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }, []);

  const platformEngagementLoading =
    chatUsage === "..." ||
    mindfulnessUsage?.[0] === "..." ||
    liveEventsUsage === "...";

  const platformEngagementData = useMemo(
    () => [
      toFiniteNumber(chatUsage),
      toFiniteNumber(mindfulnessUsage?.[0]),
      toFiniteNumber(liveEventsUsage),
    ],
    [chatUsage, mindfulnessUsage, liveEventsUsage, toFiniteNumber]
  );

  //Expansion variables
  const [isPExpanded, setIsPExpanded] = useState(false);
  const [isEExpanded, setIsEExpanded] = useState(false);
  const [isAExpanded, setIsAExpanded] = useState(false);
  const [isRExpanded, setIsRExpanded] = useState(false);
  const [isLExpanded, setIsLExpanded] = useState(false);
  const [isIExpanded, setIsIExpanded] = useState(false);

  //Functions for checking summary bars expansion
  const toggleIsPExpanded = useCallback(() => {
    setIsPExpanded((isPExpanded) => !isPExpanded);
  }, []);
  const toggleIsEExpanded = useCallback(() => {
    setIsEExpanded((isEExpanded) => !isEExpanded);
  }, []);
  const toggleIsAExpanded = useCallback(() => {
    setIsAExpanded((isAExpanded) => !isAExpanded);
  }, []);
  const toggleIsRExpanded = useCallback(() => {
    setIsRExpanded((isRExpanded) => !isRExpanded);
  }, []);
  const toggleIsLExpanded = useCallback(() => {
    setIsLExpanded((isLExpanded) => !isLExpanded);
  }, []);
  const toggleIsIExpanded = useCallback(() => {
    setIsIExpanded((isIExpanded) => !isIExpanded);
  }, []);

  const setDefaultDate = async () => {
    const current = new Date(lastCompletedMonthEnd);
    setDisplayDateString(current.toLocaleDateString("en-US", options));

    const previous = new Date(current);
    previous.setDate(0);

    setMaxDate(current);
    setFocusDate(current);
    setPreviousDate(previous);
  };

  //Set all data to default when reloading
  const newLoadData = async () => {
    setDailyEngagement("...");
    setMindfulnessUsage("...");
    setLiveEventsUsage("...");
    setChatUsage("...");
    setAssessmentUsage("...");
    setScores([0, 0, 0, 0, 0, 0]);
    setSummaries([
      "Loading Summary...",
      "Loading Summary...",
      "Loading Summary...",
      "Loading Summary...",
      "Loading Summary...",
      "Loading Summary...",
      "Loading Summary...",
    ]);
    setPearliLoad(false);
    setPDistChange([0, 0, 0, 0, 0, 0]);
    setEDistChange([0, 0, 0, 0, 0, 0]);
    setADistChange([0, 0, 0, 0, 0, 0]);
    setRDistChange([0, 0, 0, 0, 0, 0]);
    setLDistChange([0, 0, 0, 0, 0, 0]);
    setIDistChange([0, 0, 0, 0, 0, 0]);

    const summaryLoadingEl = document.getElementById("summaryloading");
    if (summaryLoadingEl) {
      summaryLoadingEl.className = "admin-section-pearli-summary-body";
    }
    const pearliSummariesEl = document.getElementById("pearlisummaries");
    if (pearliSummariesEl) {
      pearliSummariesEl.className = "hidden-div";
    }
  };

  //Change date settings from current to range, show/hide relevant data
  const handleIntervalChange = async (mode) => {
    const setClassName = (id, className) => {
      const el = document.getElementById(id);
      if (el) el.className = className;
    };

    setDataViewMode(mode);
    if (mode === "current") {
      setClassName("current", "admin-section-interval-button-active");
      setClassName("range", "admin-section-interval-button");
      setClassName("daterange", "admin-hidden");
      setClassName("current1", "admin-section-interval-button-active");
      setClassName("range1", "admin-section-interval-button");
      setClassName("daterange1", "admin-hidden");
      setClassName("current2", "admin-section-interval-button-active");
      setClassName("range2", "admin-section-interval-button");
      setClassName("daterange2", "admin-hidden");
      setClassName("current3", "admin-section-interval-button-active");
      setClassName("range3", "admin-section-interval-button");
      setClassName("daterange3", "admin-hidden");
      setClassName("pearlidistcomparison", "admin-hidden");
      setClassName("pearlidistsingle", "admin-section-item-w-bargraph-body");
      setClassName("pearliassessmentsingle", "admin-section-item-container");
      setClassName("pearliassessmentrange", "admin-hidden");

      const now = new Date();
      const currentStart = startOfMonthDate(now);
      const currentEnd = new Date(now);
      const previousStart = startOfMonthDate(
        new Date(now.getFullYear(), now.getMonth() - 1, 1)
      );
      const previousEnd = endOfMonthDate(
        new Date(now.getFullYear(), now.getMonth() - 1, 1)
      );

      setDisplayDateString(currentStart.toLocaleDateString("en-US", options));
      setPreviousDate(previousStart);
      setFocusDate(currentEnd);
      setRangePickerStart(previousStart);
      setRangePickerEnd(currentEnd);
      newLoadData();
      populateData(
        toUtcString(currentStart, false),
        toUtcString(currentEnd, true),
        toUtcString(previousStart, false),
        toUtcString(previousEnd, true)
      );
    } else if (mode === "range") {
      setClassName("current", "admin-section-interval-button");
      setClassName("range", "admin-section-interval-button-active");
      setClassName("daterange", "admin-daterange-button");
      setClassName("current1", "admin-section-interval-button");
      setClassName("range1", "admin-section-interval-button-active");
      setClassName("daterange1", "admin-daterange-button");
      setClassName("current2", "admin-section-interval-button");
      setClassName("range2", "admin-section-interval-button-active");
      setClassName("daterange2", "admin-daterange-button");
      setClassName("current3", "admin-section-interval-button");
      setClassName("range3", "admin-section-interval-button-active");
      setClassName("daterange3", "admin-range-selector");
      setClassName("pearlidistcomparison", "admin-section-item-w-bargraph-body");
      setClassName("pearlidistsingle", "admin-hidden");
      setClassName("pearliassessmentsingle", "admin-hidden");
      setClassName("pearliassessmentrange", "admin-section-item-container");

      applyAnalyticsMonthRange(
        rangeStartMonth || defaultMonthRange.startKey,
        rangeEndMonth || defaultMonthRange.endKey
      );
    }
  };

  //Change dates to range
  const setNewDates = ([startDate, endDate]) => {
    if (!startDate) {
      setRangePickerStart(null);
      setRangePickerEnd(null);
      return;
    }

    const boundedStartDate =
      startDate > lastCompletedMonthEnd ? new Date(lastCompletedMonthEnd) : new Date(startDate);

    if (!endDate) {
      setRangePickerStart(boundedStartDate);
      setRangePickerEnd(null);
      setFocusDate(boundedStartDate);
      return;
    }

    const boundedEndDate =
      endDate > lastCompletedMonthEnd ? new Date(lastCompletedMonthEnd) : new Date(endDate);

    if (boundedStartDate > boundedEndDate) {
      return;
    }

    applyAnalyticsMonthRange(
      getMonthKey(boundedStartDate),
      getMonthKey(boundedEndDate)
    );
  };

  const handleDomainRangeStartChange = (nextStartMonth) => {
    const nextEndMonth =
      rangeEndMonth && compareMonthKeys(nextStartMonth, rangeEndMonth) > 0
        ? nextStartMonth
        : rangeEndMonth;

    applyAnalyticsMonthRange(nextStartMonth, nextEndMonth);
  };

  const handleDomainRangeEndChange = (nextEndMonth) => {
    const nextStartMonth =
      rangeStartMonth && compareMonthKeys(rangeStartMonth, nextEndMonth) > 0
        ? nextEndMonth
        : rangeStartMonth;

    applyAnalyticsMonthRange(nextStartMonth, nextEndMonth);
  };

  const domains = [
    "Physical",
    "Emotional",
    "Acceptance",
    "Relationships",
    "Loving-Kindness",
    "Intelligence",
  ];

  const showComparison = dataViewMode !== "current";
  const getDiffColor = (d) => {
    if (d === undefined || d === null || isNaN(d)) return "#EF4444"; // N/A case → red
    if (d < 0) return "#EF4444"; // negative → red
    if (d > 0) return "#19B880"; // positive → green
    return "#334155"; // zero → gray
  };

  const pearliRows = useMemo(() => {
    const prev = comparisonScores?.[0] ?? [];
    const curr = comparisonScores?.[1] ?? scores ?? [];

    return domains.map((label, idx) => {
      const prevValRaw = prev[idx];
      const curValRaw = curr[idx];
      const prevVal = !isNaN(parseFloat(prevValRaw))
        ? parseFloat(prevValRaw)
        : undefined;
      const curVal = !isNaN(parseFloat(curValRaw))
        ? parseFloat(curValRaw)
        : undefined;

      let diff, pct;
      if (
        showComparison &&
        typeof prevVal === "number" &&
        typeof curVal === "number"
      ) {
        diff = Number((curVal - prevVal).toFixed(1));
        pct =
          prevVal !== 0
            ? Number((((curVal - prevVal) / prevVal) * 100).toFixed(1))
            : undefined;
      }

      return {
        label,
        prevVal,
        curVal,
        diff,
        pct,
        color: typeof diff === "number" ? getDiffColor(diff) : undefined,
      };
    });
  }, [comparisonScores, scores, dataViewMode]);

  //Populate data
  function populateData(
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
    domainMonthSelection = null
  ) {
    if (!startDate) {
      startDate = new Date();
      endDate = new Date();
      previousStartDate = new Date();
      previousEndDate = new Date();
      startDate.setDate(1);

      previousEndDate.setMonth(startDate.getMonth());
      previousEndDate.setDate(0);
      previousStartDate.setMonth(previousEndDate.getMonth());
      previousStartDate.setDate(1);

      startDate = startDate.toUTCString();
      endDate = endDate.toUTCString();
      previousStartDate = previousStartDate.toUTCString();
      previousEndDate = previousEndDate.toUTCString();
    }
    console.log(startDate, endDate);
    employeeRollCall(startDate, endDate);
    employeeEngagement(startDate, endDate);
    employeeMindfulnessUsage(startDate, endDate);
    employeeLiveEventsUsage(startDate, endDate);
    employeeChatUsage(startDate, endDate);
    employeeAssessmentUsage(startDate, endDate);
    employeePEARLIDistribution(
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
      domainMonthSelection
    );
    employeeAppEngagement(startDate, endDate);
    employeePEARLIScores(
      startDate,
      endDate,
      previousStartDate,
      previousEndDate
    );
  }

  //Get active employees and total count
  const employeeRollCall = async (startDate, endDate) => {
    try {
      const response = await api.get("/api/admin/login-activity/engagement", {
        params: {
          startDate: toLocalIsoDate(startDate),
          endDate: toLocalIsoDate(endDate),
        },
      });
      const data = response.data || {};
      setNumEmployees(Number(data.totalEmployees) || 0);
      setNumActiveEmployees(Number(data.activeEmployees) || 0);
    } catch (error) {
      console.log(error);
      setNumEmployees(0);
      setNumActiveEmployees(0);
    }
  };

  //Get active employees and total count
  const employeeEngagement = async (startDate, endDate) => {
    try {
      const response = await api.get("/api/admin/login-activity/engagement", {
        params: {
          startDate: toLocalIsoDate(startDate),
          endDate: toLocalIsoDate(endDate),
        },
      });
      const data = response.data || {};
      setDailyEngagement(Number(data.dailyEngagement) || 0);
    } catch (error) {
      console.log(error);
      setDailyEngagement(0);
    }
  };

  //Get  Employees Mindfulness Usage
  const employeeMindfulnessUsage = async (startDate, endDate) => {
    try {
      const response = await api.post("/dataView", {
        request: "mindfulnessUsage",
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });
      const data = response.data.result;
      setMindfulnessUsage(data);
    } catch (error) {
      console.log(error);
    }
  };

  //Get Employees Live Event Usage
  const employeeLiveEventsUsage = async (startDate, endDate) => {
    try {
      const response = await api.post("/dataView", {
        request: "liveEventsUsage",
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });
      const data = response.data.result;
      setLiveEventsUsage(data);
    } catch (error) {
      console.log(error);
    }
  };

  //Get Employees Live Event Usage
  const employeeChatUsage = async (startDate, endDate) => {
    try {
      const formattedStartDate = toLocalIsoDate(startDate);
      const formattedEndDate = toLocalIsoDate(endDate);

      const data = await getCompanyInterfaceUsage({
        interface: 'chat',
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        includeBreakdown: false
      });

      // Extract totalMinutes from the response
      const chatMinutes = data.totalMinutes || 0;
      setChatUsage(chatMinutes);
    } catch (error) {
      console.log('Error fetching chat usage:', error);
      setChatUsage(0);
    }
  };

  //Get Employees Assessment Usage
  const employeeAssessmentUsage = async (startDate, endDate) => {
    try {
      const fromDate = toLocalIsoDate(startDate);
      const toDate = toLocalIsoDate(endDate);

      const data = await getCompanyQuestionsAnswered({
        fromDate,
        toDate,
      });

      const questionsAnswered = Number(data?.questionsAnswered) || 0;
      setAssessmentUsage(questionsAnswered);
    } catch (error) {
      console.log('Error fetching company questions answered:', error);
      setAssessmentUsage(0);
    }
  };

  const employeeAppEngagement = async (startDate, endDate) => {
    try {
      const formattedStartDate = toLocalIsoDate(startDate);
      const formattedEndDate = toLocalIsoDate(endDate);

      const [assessmentData, liveSessionData] = await Promise.all([
        getCompanyAssessmentEngagement({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        }),
        getCompanyLiveSessionEngagement({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        }),
      ]);

      const assessmentParticipants = Number(assessmentData?.assessmentParticipants) || 0;
      const liveSessionParticipants = Number(liveSessionData?.liveSessionParticipants) || 0;

      setEngagementStats([assessmentParticipants, liveSessionParticipants]);
    } catch (error) {
      console.log('Error fetching activity participation:', error);
      setEngagementStats([0, 0]);
    }
  };
  //Get Employees Assessment Usage
  const employeePEARLIScores = async (
    startDate,
    endDate,
    previousStartDate,
    previousEndDate
  ) => {
    try {
      const response = await api.post("/dataView", {
        request: "pearliScores",
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });
      const data = response.data.result;

      if (previousStartDate && previousEndDate) {
        const responsePrevious = await api.post("/dataView", {
          request: "pearliScores",
          startDate: previousStartDate.toString(),
          endDate: previousEndDate.toString(),
        });
        const previousData = responsePrevious.data.result;
        setComparisonScores([previousData, data]);
      }
      setScores(data);

      if (data[0] >= 1) {
        const summaryResponse = await api.post("/api/assessments/analyzeOrg", {
          averages: {
            physical: data[0] || null,
            emotional: data[1] || null,
            acceptance: data[2] || null,
            relationships: data[3] || null,
            loving_kindness: data[4] || null,
            intellectual: data[5] || null,
          },
        });
        const summaryData = summaryResponse?.data?.data || {};
        setSummaries([
          summaryData.PhysicalSummary,
          summaryData.EmotionalSummary,
          summaryData.AcceptanceSummary,
          summaryData.RelationshipSummary,
          summaryData.LovingKindnessSummary,
          summaryData.IntellectualSummary,
          summaryData.GeneralSummary,
        ]);
      } else {
        setSummaries([
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
          "No PEARLI Assessment Data For This Interval.",
        ]);
      }

      const pearlisummariesEl = document.getElementById("pearlisummaries");
      const summaryloadingEl = document.getElementById("summaryloading");
      
      if (pearlisummariesEl) {
        pearlisummariesEl.className = "admin-section-item-body-pearli-container";
      }
      if (summaryloadingEl) {
        summaryloadingEl.className = "hidden-div";
      }
    } catch (error) {
      console.log(error);
      setSummaries([
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
        "Pearli summary cannot be displayed at this time. Please refresh the page.",
      ]);
    }
  };

  //Get Employees Assessment Usage
  const employeePEARLIDistribution = async (
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
    domainMonthSelection = null
  ) => {
    try {
      const normalizeDomain = (value) =>
        String(value || "")
          .trim()
          .toUpperCase()
          .replace(/\s+/g, "-");
      const domainIndex = {
        PHYSICAL: 0,
        EMOTIONAL: 1,
        ACCEPTANCE: 2,
        RELATIONSHIPS: 3,
        "LOVING-KINDNESS": 4,
        INTELLECTUAL: 5,
      };
      const toIsoDate = (value) => {
        return toLocalIsoDate(value);
      };
      const toDomainArray = (domainCounts) => {
        const counts = [0, 0, 0, 0, 0, 0];
        (domainCounts || []).forEach((entry) => {
          const key = normalizeDomain(entry.answer_domain);
          const index = domainIndex[key];
          if (index === undefined) return;
          counts[index] = Number(entry.domain_count) || 0;
        });
        return counts;
      };

      const toDate = (value) => (value instanceof Date ? value : new Date(value));
      const normalizeRange = (start, end) => {
        const s = toDate(start);
        const e = toDate(end);
        return s <= e ? [s, e] : [e, s];
      };

      let currentRequestRange = null;
      let previousRequestRange = null;

      if (
        domainMonthSelection?.startMonthKey &&
        domainMonthSelection?.endMonthKey
      ) {
        currentRequestRange = getDomainMonthApiRange(domainMonthSelection.endMonthKey);
        previousRequestRange = getDomainMonthApiRange(domainMonthSelection.startMonthKey);
      } else {
        const [currentStart, currentEnd] = normalizeRange(startDate, endDate);
        const [prevStart, prevEnd] = normalizeRange(previousStartDate, previousEndDate);

        currentRequestRange = {
          start: toIsoDate(currentStart),
          end: toIsoDate(currentEnd),
        };
        previousRequestRange = {
          start: toIsoDate(prevStart),
          end: toIsoDate(prevEnd),
        };
      }

      if (!currentRequestRange || !previousRequestRange) return;

      const [currentRes, previousRes] = await Promise.all([
        getCompanyDomainCounts({
          start_date: currentRequestRange.start,
          end_date: currentRequestRange.end,
        }),
        getCompanyDomainCounts({
          start_date: previousRequestRange.start,
          end_date: previousRequestRange.end,
        }),
      ]);

      const data = toDomainArray(currentRes?.domain_counts);
      const previousData = toDomainArray(previousRes?.domain_counts);
      var sum = 0;
      for (let i = 0; i < data.length; i++) {
        sum = sum + data[i];
      }
      setPDistChange([
        previousData[0],
        data[0],
        sum > 0 ? Math.round((data[0] / sum) * 100) : 0,
        data[0] && previousData[0] ? data[0] - previousData[0] : "N/A",
      ]);
      setEDistChange([
        previousData[1],
        data[1],
        sum > 0 ? Math.round((data[1] / sum) * 100) : 0,
        data[1] && previousData[1] ? data[1] - previousData[1] : "N/A",
      ]);
      setADistChange([
        previousData[2],
        data[2],
        sum > 0 ? Math.round((data[2] / sum) * 100) : 0,
        data[2] && previousData[2] ? data[2] - previousData[2] : "N/A",
      ]);
      setRDistChange([
        previousData[3],
        data[3],
        sum > 0 ? Math.round((data[3] / sum) * 100) : 0,
        data[3] && previousData[3] ? data[3] - previousData[3] : "N/A",
      ]);
      setLDistChange([
        previousData[4],
        data[4],
        sum > 0 ? Math.round((data[4] / sum) * 100) : 0,
        data[4] && previousData[4] ? data[4] - previousData[4] : "N/A",
      ]);
      setIDistChange([
        previousData[5],
        data[5],
        sum > 0 ? Math.round((data[5] / sum) * 100) : 0,
        data[5] && previousData[5] ? data[5] - previousData[5] : "N/A",
      ]);
      setPearliLoad(true);
    } catch (error) {
      console.log(error);
    }
  };
  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 450,
      fontFamily: "Roboto",
      fontSize: theme.typography.pxToRem(18),
      border: "1px solid #dadde9",
    },
  }));

  useEffect(() => {
    if (!showAdminButtons) {
      alert("You are not authorized to access this page");
      navigate("/home");
      return;
    }
    setIsPageVisible(true);
    populateData();
  }, [showAdminButtons, navigate]);

  return (
    <div id="adminPage" className={isPageVisible ? "admin-page" : "hidden-div"}>
      <div className="sidebar">
        <SidebarCol />
      </div>
      <div className="admin-container-wrapper">
        <div className="header">
          <MainHeader
            page={"admin"}
            titleText={"Analytics"}
            subtitleText={"Your Window Into Your Organization's Well-Being"}
          />
        </div>
        <div className="admin-content-container-mobile">
          <div className="admin-content-section">
            <div className="admin-section-group-container">
              <div className="admin-section-container">
                <div className="admin-section-header-container">
                  <div className="admin-section-header">
                    <div className="admin-section-text-container">
                      <div className="admin-section-title">
                        Analytics Unavailable on Mobile
                      </div>
                      <div className="admin-section-subtitle">
                        We're sorry, but the Analytics page is currently not
                        supported on mobile devices. For the best experience and
                        full functionality, please access this page from a
                        desktop or laptop computer. We're working on making this
                        feature mobile-friendly and appreciate your patience!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="admin-content-container">
          <div className="admin-main-content-wrapper">
            <div className="admin-index-section">
              <div className="admin-index-section-subsection-container">
                <div className="admin-index-sub-text">Home</div>
                <div className="admin-index-separator">/</div>
                <div className="admin-index-sub-text">Admin</div>
                <div className="admin-index-separator">/</div>
                <div className="admin-index-main-text">Analytics</div>
              </div>
            </div>
            {showAdminButtons && (
              <div
                className="admin-index-buttongroup"
                style={{ display: "flex", justifyContent: "flex-start", margin: "12px 0" }}
              >
                <ButtonGroup aria-label="admin-analytics-buttons">
                  <Button
                    variant={
                      location.pathname === "/admin/analytics" ? "contained" : "outlined"
                    }
                    onClick={() => navigate("/admin/analytics")}
                  >
                    Analytics
                  </Button>
                  <Button
                    variant={
                      location.pathname === "/admin/assessment-analytics" ? "contained" : "outlined"
                    }
                    onClick={() => navigate("/admin/assessment-analytics")}
                  >
                    Pearli Analytics
                  </Button>
                </ButtonGroup>
              </div>
            )}
            <div className="admin-content-section">
              <div className="admin-section-group-container">
                <div className="admin-section-container">
                  <div className="admin-section-header-container">
                    <div className="admin-section-header">
                      <div className="admin-section-text-container">
                        <div className="admin-section-title">
                          Adoption Rates and Engagement Metrics -{" "}
                          {displayDateString}
                        </div>
                        <div className="admin-section-subtitle">
                          Track platform enrollment and usage trends
                        </div>
                      </div>
                      <div className="admin-menu-container">
                        <div className="admin-section-interval-button-container">
                          <div
                            className="admin-section-interval-button-active"
                            onClick={() => handleIntervalChange("current")}
                            id="current"
                          >
                            Current Month
                          </div>
                          <div
                            className="admin-section-interval-button"
                            onClick={() => handleIntervalChange("range")}
                            id="range"
                          >
                            Month Range
                          </div>
                        </div>
                        <div className="admin-hidden" id="daterange">
                          <DatePicker
                            id="datepicker"
                            className="admin-datepicker"
                            startDate={rangePickerStart || undefined}
                            endDate={rangePickerEnd || undefined}
                            maxDate={maxDate}
                            filterDate={(date) => date <= lastCompletedMonthEnd}
                            onChange={setNewDates}
                            dateFormat={"MMMM yyyy"}
                            showMonthYearPicker
                            selectsRange
                            openToDate={focusDate}
                          />
                          <div class="admin-daterange-button-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.2 9.6C7.97333 9.6 7.78347 9.5232 7.6304 9.3696C7.4768 9.21653 7.4 9.02667 7.4 8.8C7.4 8.57333 7.4768 8.3832 7.6304 8.2296C7.78347 8.07653 7.97333 8 8.2 8C8.42667 8 8.6168 8.07653 8.7704 8.2296C8.92347 8.3832 9 8.57333 9 8.8C9 9.02667 8.92347 9.21653 8.7704 9.3696C8.6168 9.5232 8.42667 9.6 8.2 9.6ZM5 9.6C4.77333 9.6 4.5832 9.5232 4.4296 9.3696C4.27653 9.21653 4.2 9.02667 4.2 8.8C4.2 8.57333 4.27653 8.3832 4.4296 8.2296C4.5832 8.07653 4.77333 8 5 8C5.22667 8 5.4168 8.07653 5.5704 8.2296C5.72347 8.3832 5.8 8.57333 5.8 8.8C5.8 9.02667 5.72347 9.21653 5.5704 9.3696C5.4168 9.5232 5.22667 9.6 5 9.6ZM11.4 9.6C11.1733 9.6 10.9835 9.5232 10.8304 9.3696C10.6768 9.21653 10.6 9.02667 10.6 8.8C10.6 8.57333 10.6768 8.3832 10.8304 8.2296C10.9835 8.07653 11.1733 8 11.4 8C11.6267 8 11.8165 8.07653 11.9696 8.2296C12.1232 8.3832 12.2 8.57333 12.2 8.8C12.2 9.02667 12.1232 9.21653 11.9696 9.3696C11.8165 9.5232 11.6267 9.6 11.4 9.6ZM8.2 12.8C7.97333 12.8 7.78347 12.7232 7.6304 12.5696C7.4768 12.4165 7.4 12.2267 7.4 12C7.4 11.7733 7.4768 11.5835 7.6304 11.4304C7.78347 11.2768 7.97333 11.2 8.2 11.2C8.42667 11.2 8.6168 11.2768 8.7704 11.4304C8.92347 11.5835 9 11.7733 9 12C9 12.2267 8.92347 12.4165 8.7704 12.5696C8.6168 12.7232 8.42667 12.8 8.2 12.8ZM5 12.8C4.77333 12.8 4.5832 12.7232 4.4296 12.5696C4.27653 12.4165 4.2 12.2267 4.2 12C4.2 11.7733 4.27653 11.5835 4.4296 11.4304C4.5832 11.2768 4.77333 11.2 5 11.2C5.22667 11.2 5.4168 11.2768 5.5704 11.4304C5.72347 11.5835 5.8 11.7733 5.8 12C5.8 12.2267 5.72347 12.4165 5.5704 12.5696C5.4168 12.7232 5.22667 12.8 5 12.8ZM11.4 12.8C11.1733 12.8 10.9835 12.7232 10.8304 12.5696C10.6768 12.4165 10.6 12.2267 10.6 12C10.6 11.7733 10.6768 11.5835 10.8304 11.4304C10.9835 11.2768 11.1733 11.2 11.4 11.2C11.6267 11.2 11.8165 11.2768 11.9696 11.4304C12.1232 11.5835 12.2 11.7733 12.2 12C12.2 12.2267 12.1232 12.4165 11.9696 12.5696C11.8165 12.7232 11.6267 12.8 11.4 12.8ZM2.6 16C2.16 16 1.7832 15.8435 1.4696 15.5304C1.15653 15.2168 1 14.84 1 14.4V3.2C1 2.76 1.15653 2.38347 1.4696 2.0704C1.7832 1.7568 2.16 1.6 2.6 1.6H3.4V0H5V1.6H11.4V0H13V1.6H13.8C14.24 1.6 14.6168 1.7568 14.9304 2.0704C15.2435 2.38347 15.4 2.76 15.4 3.2V14.4C15.4 14.84 15.2435 15.2168 14.9304 15.5304C14.6168 15.8435 14.24 16 13.8 16H2.6ZM2.6 14.4H13.8V6.4H2.6V14.4Z"
                                fill="#AAAAAA"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="admin-section-item-container">
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Employees Enrolled
                        </div>
                        <div className="admin-section-item-subtitle">
                          Total Enrolled
                        </div>
                      </div>
                      <div className="admin-section-item-body-container">
                        <div className="admin-section-item-body-text">
                          {numEmployees}
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item-wgraph">
                      <div className="admin-section-item-wgraph-text-container">
                        <div className="admin-section-item-header">
                          <div className="admin-section-item-title">
                            Employees Active
                          </div>
                          <div className="admin-section-item-subtitle">
                            Activity Breakdown
                          </div>
                        </div>
                        <div className="admin-section-item-body-container">
                          <div className="admin-section-item-body-text">
                            {numActiveEmployees}
                          </div>
                        </div>
                      </div>
                      <div className="admin-section-item-graph">
                        <PieChart
                          loading={numEmployees === "..."}
                          series={[
                            {
                              data: [
                                {
                                  id: 0,
                                  value: numActiveEmployees,
                                  label: "Active Employees",
                                  color: "#3B82F6",
                                },
                                {
                                  id: 1,
                                  value: numEmployees - numActiveEmployees,
                                  label: "Inactive Employees",
                                  color: "#19B880",
                                },
                              ],
                              innerRadius: 40,
                              cornerRadius: 80,
                              startAngle: 90,
                              endAngle: -270,
                            },
                          ]}
                          width={115}
                          height={115}
                        />
                      </div>
                    </div>
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Daily Usage
                          <div
                            className="admin-info-icon"
                            style={{ marginLeft: "4vw" }}
                            alt="test"
                          >
                            <HtmlTooltip
                              arrow
                              title={
                                "Shows the average daily logins for the month."
                              }
                              placement="top"
                            >
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </HtmlTooltip>
                          </div>
                        </div>
                        <div className="admin-section-item-subtitle">
                          Average Daily Engagement
                        </div>
                      </div>
                      <div className="admin-section-item-body-container">
                        <div className="admin-section-item-body-text">
                          {dailyEngagement}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-section-group-container">
                <div className="admin-section-container">
                  <div className="admin-section-header-container">
                    <div className="admin-section-header">
                      <div className="admin-section-text-container">
                        <div className="admin-section-title">
                          Platform Engagement Overview
                        </div>
                        <div className="admin-section-subtitle">
                          Track how different Mindsherpa features support daily
                          well-being habits.
                        </div>
                      </div>
                      <div className="admin-menu-container">
                        <div className="admin-section-interval-button-container">
                          <div
                            className="admin-section-interval-button-active"
                            onClick={() => handleIntervalChange("current")}
                            id="current1"
                          >
                            Current Month
                          </div>
                          <div
                            className="admin-section-interval-button"
                            onClick={() => handleIntervalChange("range")}
                            id="range1"
                          >
                            Month Range
                          </div>
                        </div>
                        <div className="admin-hidden" id="daterange1">
                          <DatePicker
                            id="datepicker"
                            className="admin-datepicker"
                            startDate={rangePickerStart || undefined}
                            endDate={rangePickerEnd || undefined}
                            maxDate={maxDate}
                            filterDate={(date) => date <= lastCompletedMonthEnd}
                            onChange={setNewDates}
                            dateFormat={"MMMM yyyy"}
                            showMonthYearPicker
                            selectsRange
                            openToDate={focusDate}
                          />
                          <div class="admin-daterange-button-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.2 9.6C7.97333 9.6 7.78347 9.5232 7.6304 9.3696C7.4768 9.21653 7.4 9.02667 7.4 8.8C7.4 8.57333 7.4768 8.3832 7.6304 8.2296C7.78347 8.07653 7.97333 8 8.2 8C8.42667 8 8.6168 8.07653 8.7704 8.2296C8.92347 8.3832 9 8.57333 9 8.8C9 9.02667 8.92347 9.21653 8.7704 9.3696C8.6168 9.5232 8.42667 9.6 8.2 9.6ZM5 9.6C4.77333 9.6 4.5832 9.5232 4.4296 9.3696C4.27653 9.21653 4.2 9.02667 4.2 8.8C4.2 8.57333 4.27653 8.3832 4.4296 8.2296C4.5832 8.07653 4.77333 8 5 8C5.22667 8 5.4168 8.07653 5.5704 8.2296C5.72347 8.3832 5.8 8.57333 5.8 8.8C5.8 9.02667 5.72347 9.21653 5.5704 9.3696C5.4168 9.5232 5.22667 9.6 5 9.6ZM11.4 9.6C11.1733 9.6 10.9835 9.5232 10.8304 9.3696C10.6768 9.21653 10.6 9.02667 10.6 8.8C10.6 8.57333 10.6768 8.3832 10.8304 8.2296C10.9835 8.07653 11.1733 8 11.4 8C11.6267 8 11.8165 8.07653 11.9696 8.2296C12.1232 8.3832 12.2 8.57333 12.2 8.8C12.2 9.02667 12.1232 9.21653 11.9696 9.3696C11.8165 9.5232 11.6267 9.6 11.4 9.6ZM8.2 12.8C7.97333 12.8 7.78347 12.7232 7.6304 12.5696C7.4768 12.4165 7.4 12.2267 7.4 12C7.4 11.7733 7.4768 11.5835 7.6304 11.4304C7.78347 11.2768 7.97333 11.2 8.2 11.2C8.42667 11.2 8.6168 11.2768 8.7704 11.4304C8.92347 11.5835 9 11.7733 9 12C9 12.2267 8.92347 12.4165 8.7704 12.5696C8.6168 12.7232 8.42667 12.8 8.2 12.8ZM5 12.8C4.77333 12.8 4.5832 12.7232 4.4296 12.5696C4.27653 12.4165 4.2 12.2267 4.2 12C4.2 11.7733 4.27653 11.5835 4.4296 11.4304C4.5832 11.2768 4.77333 11.2 5 11.2C5.22667 11.2 5.4168 11.2768 5.5704 11.4304C5.72347 11.5835 5.8 11.7733 5.8 12C5.8 12.2267 5.72347 12.4165 5.5704 12.5696C5.4168 12.7232 5.22667 12.8 5 12.8ZM11.4 12.8C11.1733 12.8 10.9835 12.7232 10.8304 12.5696C10.6768 12.4165 10.6 12.2267 10.6 12C10.6 11.7733 10.6768 11.5835 10.8304 11.4304C10.9835 11.2768 11.1733 11.2 11.4 11.2C11.6267 11.2 11.8165 11.2768 11.9696 11.4304C12.1232 11.5835 12.2 11.7733 12.2 12C12.2 12.2267 12.1232 12.4165 11.9696 12.5696C11.8165 12.7232 11.6267 12.8 11.4 12.8ZM2.6 16C2.16 16 1.7832 15.8435 1.4696 15.5304C1.15653 15.2168 1 14.84 1 14.4V3.2C1 2.76 1.15653 2.38347 1.4696 2.0704C1.7832 1.7568 2.16 1.6 2.6 1.6H3.4V0H5V1.6H11.4V0H13V1.6H13.8C14.24 1.6 14.6168 1.7568 14.9304 2.0704C15.2435 2.38347 15.4 2.76 15.4 3.2V14.4C15.4 14.84 15.2435 15.2168 14.9304 15.5304C14.6168 15.8435 14.24 16 13.8 16H2.6ZM2.6 14.4H13.8V6.4H2.6V14.4Z"
                                fill="#AAAAAA"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="admin-section-item-container">
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Sherpa Usage
                        </div>
                        <div className="admin-section-item-subtitle">
                          Engagement in minutes
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text-split">
                          <div className="admin-section-item-body-text">
                            {chatUsage}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Mindfulness
                        </div>
                        <div className="admin-section-item-subtitle">
                          Engagement in minutes
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text-split">
                          <div className="admin-section-item-body-text">
                            {mindfulnessUsage[0]}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Mindfulness
                        </div>
                        <div className="admin-section-item-subtitle">
                          Number of completed exercises
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text-split">
                          <div className="admin-section-item-body-text">
                            {mindfulnessUsage[1]}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          PEARLI Assessments
                        </div>
                        <div className="admin-section-item-subtitle">
                          Number of questions answered
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text-split">
                          <div className="admin-section-item-body-text">
                            {assessmentUsage}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Live Sessions
                        </div>
                        <div className="admin-section-item-subtitle">
                          Engagement in minutes
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text-split">
                          <div className="admin-section-item-body-text">
                            {liveEventsUsage}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="admin-section-item-container">
                    <div className="admin-section-item">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Platform Engagement
                        </div>
                        <div className="admin-section-item-subtitle">
                          Total time spent per feature in minutes
                        </div>
                      </div>
                      <div className="admin-section-item-split-body-container">
                        <div className="admin-section-item-body-text">
                          <BarChart
                            loading={platformEngagementLoading}
                            xAxis={[
                              {
                                scaleType: "band",
                                data: ["Chat", "Mindfulness", "Live Sessions"],
                                label:
                                  "Chat drives 84% of activity; Live sessions underutilized at 20%",
                              },
                            ]}
                            yAxis={[{ label: "Total Minutes" }]}
                            series={[
                              {
                                data: platformEngagementData,
                                color: "#60A5FA",
                              },
                            ]}
                            height={300}
                            width={472}
                            barLabel={(item) => {
                              if (item.value > 0) {
                                return item.value?.toString() + " min.";
                              } else {
                                return "";
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item-pie">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          PEARLI Assessment Engagement
                        </div>
                      </div>
                      <div className="admin-section-item-body-container-graph">
                        <div className="admin-piechart">
                          <p className="admin-piechart-text">
                            {engagementStats[0]}/{numActiveEmployees}
                          </p>
                          <p className="admin-piechart-subtext">
                            Assessments Taken
                          </p>
                          <PieChart
                            loading={numEmployees === "..."}
                            series={[
                              {
                                data: [
                                  {
                                    id: 0,
                                    value:
                                      numActiveEmployees && engagementStats
                                        ? engagementStats[0]
                                        : 0,
                                    color: "#19B880",
                                  },
                                  {
                                    id: 1,
                                    value:
                                      numActiveEmployees && engagementStats
                                        ? numActiveEmployees -
                                        engagementStats[0]
                                        : 0,
                                    color: "#D3F8E4",
                                  },
                                ],
                                innerRadius: 70,
                                cornerRadius: 80,
                                startAngle: -90,
                                endAngle: 270,
                              },
                            ]}
                            width={200}
                            height={200}
                          />
                        </div>
                        <div className="admin-pie-body-text">
                          {Math.round(
                            (numActiveEmployees && engagementStats
                              ? engagementStats[0] / numActiveEmployees
                              : 0) * 100,
                            1
                          )}
                          % Engagement
                        </div>
                      </div>
                    </div>
                    <div className="admin-section-item-pie">
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Live Session Engagement
                        </div>
                      </div>
                      <div className="admin-section-item-body-container-graph">
                        <div className="admin-piechart">
                          <p className="admin-piechart-text">
                            {engagementStats[1]}/{numActiveEmployees}
                          </p>
                          <p className="admin-piechart-subtext">
                            Attended Sessions
                          </p>
                          <PieChart
                            loading={numEmployees === "..."}
                            series={[
                              {
                                data: [
                                  {
                                    id: 0,
                                    value:
                                      numActiveEmployees && engagementStats
                                        ? engagementStats[1]
                                        : 0,
                                    color: "#19B880",
                                  },
                                  {
                                    id: 1,
                                    value:
                                      numActiveEmployees && engagementStats
                                        ? numActiveEmployees -
                                        engagementStats[1]
                                        : 0,
                                    color: "#D3F8E4",
                                  },
                                ],
                                innerRadius: 70,
                                cornerRadius: 80,
                                startAngle: -90,
                                endAngle: 270,
                              },
                            ]}
                            width={200}
                            height={200}
                          />
                        </div>
                        <div className="admin-pie-body-text">
                          {Math.round(
                            (numActiveEmployees && engagementStats
                              ? engagementStats[1] / numActiveEmployees
                              : 0) * 100,
                            1
                          )}
                          % Engagement
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <WellnessCheckinSection isAdmin />

              <div className="admin-section-group-container">
                <div className="admin-section-container">
                  <div className="admin-section-header-container">
                    <div className="admin-section-header">
                      <div className="admin-section-text-container">
                        <div className="admin-section-title">
                          PEARLI Domain of Focus
                        </div>
                        <div className="admin-section-subtitle">
                          Tracking Employees Domain of Focus across PEARLI
                        </div>
                      </div>
                      <div className="admin-menu-container admin-menu-container-domain">
                        <div className="admin-section-interval-button-container">
                          <div
                            className="admin-section-interval-button-active"
                            onClick={() => handleIntervalChange("current")}
                            id="current3"
                          >
                            Current Month
                          </div>
                          <div
                            className="admin-section-interval-button"
                            onClick={() => handleIntervalChange("range")}
                            id="range3"
                          >
                            Month Range
                          </div>
                        </div>
                        <div className="admin-hidden" id="daterange3">
                          <MonthRangeSelector
                            monthOptions={monthOptions}
                            startMonth={rangeStartMonth}
                            endMonth={rangeEndMonth}
                            onChangeStart={handleDomainRangeStartChange}
                            onChangeEnd={handleDomainRangeEndChange}
                            sx={{ mt: 0 }}
                            anchorDate={today}
                            allowCurrentMonth
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="admin-section-item-container">
                    <div
                      className="admin-section-item-w-bargraph"
                      style={{ height: "440px" }}
                    >
                      <div className="admin-section-item-header">
                        <div className="admin-section-item-title">
                          Domain Engagement Overview ({displayDateString})
                        </div>
                        <div className="admin-section-item-subtitle">
                          Data reflects distribution of user selected primary
                          PEARLI domain of focus
                        </div>
                      </div>
                      <div className="admin-hidden" id="pearlidistcomparison">
                        <div className="admin-section-item-body-text">
                          <BarChart
                            loading={!pearliLoad}
                            xAxis={[
                              {
                                scaleType: "band",
                                data: [
                                  "Physical",
                                  "Emotional",
                                  "Acceptance",
                                  "Relationship",
                                  "Loving-Kindness",
                                  "Intelligence",
                                ],
                              },
                            ]}
                            yAxis={[{ label: "# of Selections", width: 50 }]}
                            series={[
                              {
                                data: [
                                  pDistChange[0],
                                  eDistChange[0],
                                  aDistChange[0],
                                  rDistChange[0],
                                  lDistChange[0],
                                  iDistChange[0],
                                ],
                                color: "#3B82F6",
                                label: focusDate
                                  ? previousDate.toLocaleDateString(
                                    "en-US",
                                    options
                                  )
                                  : "Loading",
                              },
                              {
                                data: [
                                  pDistChange[1],
                                  eDistChange[1],
                                  aDistChange[1],
                                  rDistChange[1],
                                  lDistChange[1],
                                  iDistChange[1],
                                ],
                                color: "#0C8F63",
                                label: focusDate
                                  ? focusDate.toLocaleDateString(
                                    "en-US",
                                    options
                                  )
                                  : "Loading",
                              },
                            ]}
                            height={296}
                            width={650}
                          />
                        </div>
                      </div>
                      <div
                        className="admin-section-item-w-bargraph-body"
                        id="pearlidistsingle"
                      >
                        <div className="admin-section-item-body-text">
                          <BarChart
                            loading={!pearliLoad}
                            xAxis={[
                              {
                                scaleType: "band",
                                data: [
                                  "Physical",
                                  "Emotional",
                                  "Acceptance",
                                  "Relationship",
                                  "Loving-Kindness",
                                  "Intelligence",
                                ],
                              },
                            ]}
                            yAxis={[{ label: "# of Selections", width: 50 }]}
                            series={[
                              {
                                data: [
                                  pDistChange[1],
                                  eDistChange[1],
                                  aDistChange[1],
                                  rDistChange[1],
                                  lDistChange[1],
                                  iDistChange[1],
                                ],
                                color: "#3B82F6",
                                label: displayDateString,
                              },
                            ]}
                            height={296}
                            width={650}
                          />
                        </div>
                      </div>
                    </div>

                    <DomainEngagementTable
                      displayDateString={displayDateString}
                      dataViewMode={dataViewMode}
                      focusDate={focusDate}
                      previousDate={previousDate}
                      optionsShort={optionsShort}
                      pDistChange={pDistChange}
                      eDistChange={eDistChange}
                      aDistChange={aDistChange}
                      rDistChange={rDistChange}
                      lDistChange={lDistChange}
                      iDistChange={iDistChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
